// liveStreamChatActions.js
'use strict';
import fetch from '../resource/customFetch.js';
import { v4 as uuidv4 } from 'uuid';

import {
  ADD_LIST_ITEMS,
  REMOVE_LIST_ITEMS,
  MERGE_LIVE_STREAM_DATA,
  SET_LIVE_STREAM_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  SET_OPERATION_DATA,
} from '../ActionTypes.js';
import getMeData from '../selector/getMeData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getListData from '../selector/getListData.js';
import getMeBalance from '../selector/getMeBalance.js';
import getOperationData from '../selector/getOperationData.js';
import getLivestreamChatPrice from '../selector/getLivestreamChatPrice.js';
import getSettingsData from '../selector/getSettingsData.js';
import addvalue from '../action/addvalue.js';
import pushToastr from '../action/pushToastr.js';
import deductDiamond from '../action/deductDiamond.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import {
  PREVIOUS_CHATS_LIMITATION,
  CHAT_QUEUE_LIMITATION,
  CHAT_DEBOUNCE_SECONDS,
} from '../resource/liveStreamConstants.js';
import getCurrentUserLevel from '../resource/getCurrentUserLevel.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';

let timer = null;
let previousSubmitTimestamp = null;

/**
 * Dequeue live stream chat
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
const dequeueLiveStreamChat =
  ({ streamId }) =>
  async (dispatch, getState) => {
    const networkingSelectPath = ['liveStream', streamId, 'chat'];
    const token = getMeData(getState(), 'token');
    const chatIds =
      getListData(
        getState(),
        ['liveStream', streamId, 'queue'],
        'itemIds'
      )?.slice(0, CHAT_QUEUE_LIMITATION) || [];

    if (!token || chatIds.length === 0) {
      return dispatch({ type: '' });
    }

    const chats = chatIds
      .map(chatId => {
        const chat = getLiveStreamData(getState(), [streamId, 'chat'], chatId);

        if (chat) {
          return {
            id: chatId,
            text: chat.text,
            timestamp: chat.timestamp,
            pinned: chat.pinned,
          };
        }

        return null;
      })
      .filter(chat => chat != null && chat !== '');

    const fetchOptions = {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(chats),
    };

    const url = getResourceUrl({ endpoint: `/streams/${streamId}/chat` });

    dispatch({
      type: REMOVE_LIST_ITEMS,
      payload: {
        selectPath: ['liveStream', streamId, 'queue'],
        itemIds: chatIds,
      },
    });
    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });

    try {
      if (chats?.length > 0) {
        let response = await fetch(url.href, fetchOptions);

        if (!response.ok) {
          response = await handleFetchError({
            response,
            dispatch,
            getState,
            fetchOptions,
            fetchUrl: url,
          });
        }
      }

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      let errorObject = {};
      try {
        errorObject = JSON.parse(error.message);
        errorObject.code = errorObject.code?.toLowerCase?.();
        // eslint-disable-next-line no-empty
      } catch (_) {}

      if (
        403 === errorObject.status &&
        'insufficient_funds' === errorObject.code
      ) {
        dispatch(
          pushToastr({
            textKey: errorObject.code,
            color: 'error',
          })
        );
        dispatch(addvalue({ via: 'livestream-chat' }));
      }

      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

/**
 * Enqueue live stream chat.
 * @kind action
 * @param {string} {streamId} - stream id.
 * @param {string} {value} - chat value.
 * @return {Promise} Action promise.
 */
export const enqueueLiveStreamChat =
  ({ streamId, value }) =>
  async (dispatch, getState) => {
    const meId = getMeData(getState(), 'id');
    const isStreamer = meId === streamId;
    const balance = getMeBalance(getState());
    const chatPrice = getLivestreamChatPrice(getState(), streamId);
    if (!isStreamer) {
      const hasEnoughBalance = balance >= chatPrice;
      if (!hasEnoughBalance) {
        dispatch(
          pushToastr({
            textKey: 'alert_insufficient_funds',
            color: 'error',
          })
        );
        return dispatch(addvalue({ via: 'livestream-chat' }));
      }
      dispatch(deductDiamond({ price: chatPrice }));
    }
    const previousChats =
      getLiveStreamData(getState(), [streamId], 'previousChats') || [];
    dispatch({
      type: SET_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'previousChats'],
        data: [...previousChats, value].slice(-PREVIOUS_CHATS_LIMITATION),
      },
    });
    const chatId = uuidv4();
    const timestampOffsetSeconds = getTimestampOffset(
      getState(),
      Accuracy.SECOND
    );
    const timestamp = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    const needsLivestreamEffect =
      getSettingsData(getState(), 'toggles')?.['livestream_effect'] || false;
    let senderLevel = undefined;
    if (needsLivestreamEffect) {
      senderLevel = getCurrentUserLevel({
        levelData: getMeData(getState(), 'level'),
        unixTimestamp: timestamp,
      });
    }
    const shouldPinMessage = isStreamer
      ? !!getOperationData(getState(), [streamId], 'shouldPinMessage')
      : false;
    dispatch({
      type: MERGE_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'chat', chatId],
        data: {
          id: chatId,
          text: value,
          senderId: meId,
          senderLevel,
          timestamp,
          pinned: shouldPinMessage,
        },
      },
    });

    if (isStreamer && shouldPinMessage) {
      // Set notice
      dispatch({
        type: SET_LIVE_STREAM_DATA,
        payload: {
          selectPath: [streamId, 'pinned'],
          data: [
            {
              id: chatId,
              text: value,
              senderId: meId,
              timestamp,
            },
          ],
        },
      });
    } else {
      dispatch({
        type: ADD_LIST_ITEMS,
        payload: {
          selectPath: ['liveStream', streamId, 'chat'],
          itemIds: [chatId],
        },
      });
    }
    dispatch({
      type: ADD_LIST_ITEMS,
      payload: {
        selectPath: ['liveStream', streamId, 'queue'],
        itemIds: [chatId],
      },
    });
    dispatch(setLivestreamChatDraft({ streamId, value: '' }));
    return dispatch(processLiveStreamChatQueue({ streamId }));
  };

/**
 * Process live stream chat queue
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
const processLiveStreamChatQueue =
  ({ streamId }) =>
  async (dispatch, getState) => {
    const queuedChatIds =
      getListData(getState(), ['liveStream', streamId, 'queue'], 'itemIds') ||
      [];

    if (queuedChatIds.length === 0) {
      return dispatch({ type: '' });
    }

    const now = getCurrentUnixTimestamp();
    const canSubmitImmediately =
      now > previousSubmitTimestamp + CHAT_DEBOUNCE_SECONDS ||
      queuedChatIds.length >= CHAT_QUEUE_LIMITATION;

    if (canSubmitImmediately) {
      previousSubmitTimestamp = now;

      dispatch(clearTimer());
      dispatch(dequeueLiveStreamChat({ streamId }));
      return dispatch(processLiveStreamChatQueue({ streamId }));
    }

    dispatch(setTimer({ streamId }));
  };

/**
 * Clear live stream chat queue
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
export const clearLiveStreamChatQueue =
  ({ streamId }) =>
  async (dispatch, getState) => {
    const chatIds =
      getListData(getState(), ['liveStream', streamId, 'queue'], 'itemIds') ||
      [];

    dispatch(clearTimer());
    return dispatch({
      type: REMOVE_LIST_ITEMS,
      payload: {
        selectPath: ['liveStream', streamId, 'queue'],
        itemIds: chatIds,
      },
    });
  };

/**
 * Set live stream timer
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
const setTimer =
  ({ streamId }) =>
  async (dispatch, getState) => {
    const queuedChatIds =
      getListData(getState(), ['liveStream', streamId, 'queue'], 'itemIds') ||
      [];
    const now = getCurrentUnixTimestamp();

    if (queuedChatIds.length === 0) {
      return dispatch({ type: '' });
    }

    if (!timer) {
      timer = setTimeout(
        () => {
          dispatch(dequeueLiveStreamChat({ streamId }));
        },
        (previousSubmitTimestamp + CHAT_DEBOUNCE_SECONDS - now) * 1000
      );
    }
  };

/**
 * Clear live stream timer
 * @kind action
 * @return {Promise} Action promise.
 */
const clearTimer = () => async () => {
  clearTimeout(timer);
  timer = null;
};

/**
 * Set live stream input draft
 * @kind action
 * @return {Promise} Action promise.
 */
export const setLivestreamChatDraft =
  ({ streamId, value }) =>
  async dispatch => {
    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: [streamId, 'chatDraft'],
        data: value,
      },
    });
  };

/**
 * Set live stream input toastr
 * @kind action
 * @return {Promise} Action promise.
 */
export const setLivestreamChatToastr =
  ({ streamId, i18nKey }) =>
  async dispatch => {
    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: [streamId, 'chatToastr'],
        data: { i18nKey },
      },
    });
  };
